/**
 * ChatBubble — one turn of a conversation.
 *
 * TURN ANATOMY (docs/architecture/chat-ui-direction.md §3, adopted 2026-08-17).
 * The two turns are deliberately asymmetric, the way Claude / ChatGPT / Gemini
 * all converged:
 *
 *   user      compact right-aligned `bg-primary` capsule, ~70% max width,
 *             plain text. Unchanged.
 *   assistant a full-width DOCUMENT on the page ground — no capsule. The tinted
 *             box is an iMessage inheritance that fights structured answers: it
 *             caps the width, wraps tables badly, and signals "short message"
 *             when the content is a report. The surface's reading column sets
 *             the measure now; the turn renderer does not set its own.
 *
 * The action row moved from an absolutely-positioned `-right-9` gutter to an
 * INLINE row below the turn. The gutter sat outside the turn's box, so it was
 * clipped by every `overflow` container the transcript renders inside — the
 * widget panel and the mobile sheet, i.e. the two surfaces where copy matters
 * most. Reveal is CSS (`group-hover` / `focus-within`) rather than a hover
 * state, so the buttons are reachable by keyboard with no pointer at all, and
 * on touch devices — which have no hover to reveal anything — the row is
 * permanently visible at reduced opacity.
 */
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { MonoLabel } from '@/components/editorial';
import { Copy, Check, Clock, AlertCircle, Bookmark } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AGENT_NAMES, AGENT_INTERNAL_NAMES } from '@/lib/constants';
import { COMPANION_NAME } from '@/lib/companion-config';
import { formatClock, intentLabel } from '@/lib/chat-display';
import { useAuth } from '@/contexts/AuthContext';
import { ChatMarkdown, type ChatDensity } from './ChatMarkdown';
import type { ChatMessageState } from '@/types/chat';

interface ChatBubbleProps {
  message: ChatMessageState;
  isGrouped?: boolean;
  showTimestamp?: boolean;
  personaName?: string;
  isBookmarked?: boolean;
  onBookmark?: (messageId: string) => void;
  /**
   * Reading density. Parent surfaces stay editorial; staff surfaces
   * (Williams / ledger / handoff) run denser. Passed through to ChatMarkdown.
   */
  density?: ChatDensity;
}

function ChatBubbleInner({
  message,
  isGrouped = false,
  showTimestamp = true,
  personaName = COMPANION_NAME,
  isBookmarked = false,
  onBookmark,
  density = 'comfortable',
}: ChatBubbleProps) {
  const { t } = useTranslation('chat');
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isUser = message.role === 'user';
  const isCompact = density === 'compact';

  const agentName = isAdmin
    ? (AGENT_INTERNAL_NAMES[message.metadata?.agent ?? ''] ?? message.metadata?.agent ?? personaName)
    : (AGENT_NAMES[message.metadata?.agent ?? ''] ?? personaName);

  // Each turn is a labelled group so a screen reader can navigate turn-by-turn
  // (direction doc §9). Speaker first, then the time it landed.
  const speaker = isUser ? t('core.bubble.userLabel') : agentName;
  const spokenTime = message.timestamp ? formatClock(message.timestamp) : '';
  const turnLabel = spokenTime ? `${speaker}, ${spokenTime}` : speaker;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showActions = !isUser && message.status === 'complete';

  return (
    <div
      role="group"
      aria-label={turnLabel}
      className={cn(
        'flex gap-3 group',
        'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200',
        isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={cn(
          'flex flex-col',
          // The user turn keeps the capsule's measure; the assistant turn takes
          // the column it is given and lets the surface decide the measure.
          isUser ? 'items-end max-w-[80%] md:max-w-[70%]' : 'w-full min-w-0'
        )}
      >
        {!isUser && !isGrouped && (
          <div className={cn('flex items-center gap-2', isCompact ? 'mb-0.5' : 'mb-1')}>
            <span className="text-caption font-medium text-stevens-ink-3">{agentName}</span>
            {isAdmin && intentLabel(message.metadata?.intent) && (
              <MonoLabel className="text-stevens-ink-3/80 px-1.5 py-0.5 rounded border border-border/60">
                {intentLabel(message.metadata?.intent)}
              </MonoLabel>
            )}
          </div>
        )}

        {isUser ? (
          <div
            className={cn(
              'rounded-lg bg-primary text-primary-foreground',
              isCompact ? 'px-3 py-2' : 'px-4 py-2.5'
            )}
          >
            <div className="whitespace-pre-wrap break-words text-body">{message.content}</div>
          </div>
        ) : (
          <ChatMarkdown content={message.content} status={message.status} density={density} />
        )}

        {showActions && (
          <div
            className={cn(
              'flex items-center gap-0.5 -ml-1.5',
              isCompact ? 'mt-0.5' : 'mt-1',
              // Hover, keyboard focus, or a bookmarked turn reveals the row.
              // Touch devices have no hover at all, so there it simply stays.
              'opacity-0 group-hover:opacity-100 focus-within:opacity-100',
              '[@media(hover:none)]:opacity-60',
              isBookmarked && 'opacity-100',
              'motion-safe:transition-opacity motion-safe:duration-200'
            )}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-60 hover:opacity-100"
              onClick={handleCopy}
              aria-label={t('core.actions.copyMessage')}
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-status-ok-fg" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
            {onBookmark && (
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-7 w-7 hover:opacity-100', isBookmarked ? 'opacity-100' : 'opacity-60')}
                onClick={() => onBookmark(message.id)}
                aria-label={isBookmarked ? t('transcript.removeBookmark') : t('transcript.bookmark')}
              >
                <Bookmark className={cn('h-3.5 w-3.5', isBookmarked && 'fill-primary text-primary')} />
              </Button>
            )}
          </div>
        )}

        <div className={cn('flex items-center gap-1.5', isCompact ? 'mt-0.5' : 'mt-1')}>
          {message.status === 'sending' && (
            <Clock className="h-3 w-3 text-stevens-ink-3 animate-pulse" />
          )}
          {message.status === 'error' && (
            <AlertCircle className="h-3 w-3 text-destructive" />
          )}
          {showTimestamp && message.timestamp && (
            <span className="text-caption text-stevens-ink-3">{formatClock(message.timestamp)}</span>
          )}
        </div>
      </div>

    </div>
  );
}

export const ChatBubble = memo(ChatBubbleInner, (prev, next) => {
  return (
    prev.message.id === next.message.id &&
    prev.message.content === next.message.content &&
    prev.message.status === next.message.status &&
    prev.isGrouped === next.isGrouped &&
    prev.showTimestamp === next.showTimestamp &&
    prev.isBookmarked === next.isBookmarked &&
    prev.density === next.density
  );
});
